import { T_PANEL, cellKey } from './schema';

// Математика сетки корпуса. Все размеры в сантиметрах.
// Схема: стенки корпуса толщиной T_PANEL по периметру. Внутреннее поле:
//   innerX = T_PANEL, innerY = T_PANEL
//   innerW = body.width  - 2*T_PANEL
//   innerH = body.height - 2*T_PANEL
// Колонки и ряды заданы фракциями (`frac`) или абсолютными (`abs`, см). abs приоритетнее.
// Перегородки между колонками — T_PANEL. Горизонты между рядами — T_PANEL.

export function innerRect(body) {
  return {
    x: T_PANEL,
    y: T_PANEL,
    w: Math.max(0, body.width  - 2 * T_PANEL),
    h: Math.max(0, body.height - 2 * T_PANEL),
  };
}

// Возвращает массив границ колонок в виде { x, w } (x — левый край колонки, w — её ширина).
export function computeColRects(body, grid) {
  const { x: innerX, w: innerW } = innerRect(body);
  const cols = grid.cols || [{ frac: 1 }];
  const n = cols.length;
  const dividerTotal = T_PANEL * Math.max(0, n - 1);
  const available = Math.max(0, innerW - dividerTotal);

  // абсолютные сначала
  let fixedSum = 0;
  let fracSum = 0;
  cols.forEach(c => {
    if (c.abs != null) fixedSum += c.abs;
    else fracSum += (c.frac || 0);
  });
  const flexAvail = Math.max(0, available - fixedSum);

  const widths = cols.map(c => {
    if (c.abs != null) return c.abs;
    if (fracSum <= 0) return 0;
    return flexAvail * ((c.frac || 0) / fracSum);
  });

  const rects = [];
  let cursor = innerX;
  for (let i = 0; i < n; i++) {
    rects.push({ x: cursor, w: widths[i] });
    cursor += widths[i];
    if (i < n - 1) cursor += T_PANEL; // перегородка
  }
  return rects;
}

// Аналогично для рядов. Ряд 0 — вверху, y растёт вниз.
export function computeRowRects(body, grid) {
  const { y: innerY, h: innerH } = innerRect(body);
  const rows = grid.rows || [{ frac: 1 }];
  const n = rows.length;
  const dividerTotal = T_PANEL * Math.max(0, n - 1);
  const available = Math.max(0, innerH - dividerTotal);

  let fixedSum = 0;
  let fracSum = 0;
  rows.forEach(r => {
    if (r.abs != null) fixedSum += r.abs;
    else fracSum += (r.frac || 0);
  });
  const flexAvail = Math.max(0, available - fixedSum);

  const heights = rows.map(r => {
    if (r.abs != null) return r.abs;
    if (fracSum <= 0) return 0;
    return flexAvail * ((r.frac || 0) / fracSum);
  });

  const rects = [];
  let cursor = innerY;
  for (let i = 0; i < n; i++) {
    rects.push({ y: cursor, h: heights[i] });
    cursor += heights[i];
    if (i < n - 1) cursor += T_PANEL;
  }
  return rects;
}

// Прямоугольник одной ячейки в координатах корпуса (x,y от левого-верхнего угла).
export function cellRect(body, grid, col, row) {
  const colR = computeColRects(body, grid)[col];
  const rowR = computeRowRects(body, grid)[row];
  if (!colR || !rowR) return null;
  return { x: colR.x, y: rowR.y, w: colR.w, h: rowR.h };
}

// Поделить колонку `col` на 2 равные части. cells разделяются: содержимое старой колонки уходит в левую,
// новая правая по умолчанию пустая.
export function splitColumn(grid, cells, col) {
  const cols = grid.cols.slice();
  const cur = cols[col];
  let left, right;
  if (cur.abs != null) {
    const half = cur.abs / 2;
    left = { abs: half }; right = { abs: half };
  } else {
    const half = (cur.frac || 1) / 2;
    left = { frac: half }; right = { frac: half };
  }
  cols.splice(col, 1, left, right);

  // сдвигаем cells: те, у кого c > col, сдвигаются на +1
  const newCells = {};
  const nRows = grid.rows.length;
  Object.entries(cells).forEach(([k, v]) => {
    const [c, r] = k.split(',').map(Number);
    if (c === col) {
      newCells[cellKey(c, r)] = { ...v };            // старое наполнение осталось в левой
      newCells[cellKey(c + 1, r)] = { fill: 'empty' };
    } else if (c > col) {
      newCells[cellKey(c + 1, r)] = { ...v };
    } else {
      newCells[cellKey(c, r)] = { ...v };
    }
  });
  // На случай, если каких-то ячеек вообще не было — заполним пустыми
  for (let c = 0; c < cols.length; c++) {
    for (let r = 0; r < nRows; r++) {
      if (!newCells[cellKey(c, r)]) newCells[cellKey(c, r)] = { fill: 'empty' };
    }
  }
  return { cols, cells: newCells };
}

export function splitRow(grid, cells, row) {
  const rows = grid.rows.slice();
  const cur = rows[row];
  let top, bot;
  if (cur.abs != null) {
    const half = cur.abs / 2;
    top = { abs: half }; bot = { abs: half };
  } else {
    const half = (cur.frac || 1) / 2;
    top = { frac: half }; bot = { frac: half };
  }
  rows.splice(row, 1, top, bot);

  const newCells = {};
  const nCols = grid.cols.length;
  Object.entries(cells).forEach(([k, v]) => {
    const [c, r] = k.split(',').map(Number);
    if (r === row) {
      newCells[cellKey(c, r)] = { ...v };
      newCells[cellKey(c, r + 1)] = { fill: 'empty' };
    } else if (r > row) {
      newCells[cellKey(c, r + 1)] = { ...v };
    } else {
      newCells[cellKey(c, r)] = { ...v };
    }
  });
  for (let c = 0; c < nCols; c++) {
    for (let r = 0; r < rows.length; r++) {
      if (!newCells[cellKey(c, r)]) newCells[cellKey(c, r)] = { fill: 'empty' };
    }
  }
  return { rows, cells: newCells };
}

export function removeColumn(grid, cells, col) {
  if (grid.cols.length <= 1) return { cols: grid.cols, cells };
  const cols = grid.cols.slice();
  cols.splice(col, 1);
  const newCells = {};
  Object.entries(cells).forEach(([k, v]) => {
    const [c, r] = k.split(',').map(Number);
    if (c === col) return;
    const nc = c > col ? c - 1 : c;
    newCells[cellKey(nc, r)] = { ...v };
  });
  return { cols, cells: newCells };
}

export function removeRow(grid, cells, row) {
  if (grid.rows.length <= 1) return { rows: grid.rows, cells };
  const rows = grid.rows.slice();
  rows.splice(row, 1);
  const newCells = {};
  Object.entries(cells).forEach(([k, v]) => {
    const [c, r] = k.split(',').map(Number);
    if (r === row) return;
    const nr = r > row ? r - 1 : r;
    newCells[cellKey(c, nr)] = { ...v };
  });
  return { rows, cells: newCells };
}
